import { ProjectRoles, WorkspaceUserRoles } from '../enums';
export var PermissionKey;
(function (PermissionKey) {
    PermissionKey["TABLE_VISIBILITY"] = "TABLE_VISIBILITY";
    PermissionKey["TABLE_RECORD_ADD"] = "TABLE_RECORD_ADD";
    PermissionKey["TABLE_RECORD_DELETE"] = "TABLE_RECORD_DELETE";
    PermissionKey["RECORD_FIELD_EDIT"] = "RECORD_FIELD_EDIT";
    PermissionKey["DOCUMENT_VISIBILITY"] = "DOCUMENT_VISIBILITY";
    PermissionKey["DOCUMENT_EDIT"] = "DOCUMENT_EDIT";
})(PermissionKey || (PermissionKey = {}));
export var PermissionGrantedType;
(function (PermissionGrantedType) {
    PermissionGrantedType["ROLE"] = "role";
    PermissionGrantedType["USER"] = "user";
    PermissionGrantedType["NOBODY"] = "nobody";
})(PermissionGrantedType || (PermissionGrantedType = {}));
export var PermissionEntity;
(function (PermissionEntity) {
    PermissionEntity["TABLE"] = "table";
    PermissionEntity["FIELD"] = "field";
    PermissionEntity["DOCUMENT"] = "document";
})(PermissionEntity || (PermissionEntity = {}));
export var PermissionRole;
(function (PermissionRole) {
    PermissionRole["OWNER"] = "owner";
    PermissionRole["CREATOR"] = "creator";
    PermissionRole["EDITOR"] = "editor";
    PermissionRole["COMMENTER"] = "commenter";
    PermissionRole["VIEWER"] = "viewer";
})(PermissionRole || (PermissionRole = {}));
// Permission option values used across the application
export var PermissionOptionValue;
(function (PermissionOptionValue) {
    PermissionOptionValue["VIEWERS_AND_UP"] = "viewers_and_up";
    PermissionOptionValue["COMMENTERS_AND_UP"] = "commenters_and_up";
    PermissionOptionValue["EDITORS_AND_UP"] = "editors_and_up";
    PermissionOptionValue["CREATORS_AND_UP"] = "creators_and_up";
    PermissionOptionValue["SPECIFIC_USERS"] = "specific_users";
    PermissionOptionValue["NOBODY"] = "nobody";
    PermissionOptionValue["EVERYONE"] = "everyone";
})(PermissionOptionValue || (PermissionOptionValue = {}));
export const PermissionOptions = [
    {
        value: PermissionOptionValue.CREATORS_AND_UP,
        label: 'Creators & up',
        description: 'Members with Creator or Owner role',
        icon: 'role_creator',
    },
    {
        value: PermissionOptionValue.EDITORS_AND_UP,
        label: 'Editors & up',
        description: 'Members with Editor, Creator or Owner role',
        icon: 'role_editor',
        isDefault: true,
    },
    {
        value: PermissionOptionValue.VIEWERS_AND_UP,
        label: 'Viewers and up',
        description: 'Members with Viewer, Commenter, Editor, Creator or Owner role',
        icon: 'role_viewer',
    },
    {
        value: PermissionOptionValue.SPECIFIC_USERS,
        label: 'Specific users',
        description: 'Specific set of members',
        icon: 'ncUsers',
    },
    {
        value: PermissionOptionValue.EVERYONE,
        label: 'Everyone',
        description: 'All members can access',
        icon: 'ncUsers',
        isDefault: true,
    },
    {
        value: PermissionOptionValue.NOBODY,
        label: 'Nobody',
        description: 'No access for anyone',
        icon: 'role_no_access',
    },
];
export const PermissionRolePower = {
    [PermissionRole.OWNER]: 6,
    [PermissionRole.CREATOR]: 5,
    [PermissionRole.EDITOR]: 4,
    [PermissionRole.COMMENTER]: 3,
    [PermissionRole.VIEWER]: 2,
};
export const PermissionRoleMap = {
    [ProjectRoles.OWNER]: PermissionRole.OWNER,
    [ProjectRoles.CREATOR]: PermissionRole.CREATOR,
    [ProjectRoles.EDITOR]: PermissionRole.EDITOR,
    [ProjectRoles.COMMENTER]: PermissionRole.COMMENTER,
    [ProjectRoles.VIEWER]: PermissionRole.VIEWER,
    [WorkspaceUserRoles.OWNER]: PermissionRole.OWNER,
    [WorkspaceUserRoles.CREATOR]: PermissionRole.CREATOR,
    [WorkspaceUserRoles.EDITOR]: PermissionRole.EDITOR,
    [WorkspaceUserRoles.COMMENTER]: PermissionRole.COMMENTER,
    [WorkspaceUserRoles.VIEWER]: PermissionRole.VIEWER,
};
export const PermissionMeta = {
    [PermissionKey.TABLE_VISIBILITY]: {
        minimumRole: PermissionRole.VIEWER,
        label: 'Who can view table',
        description: 'can view table',
        userSelectorDescription: 'Only members selected here will be able to view and access this table.',
    },
    [PermissionKey.TABLE_RECORD_ADD]: {
        minimumRole: PermissionRole.EDITOR,
        label: 'Who can create records',
        description: 'can create records',
        userSelectorDescription: 'Only members selected here will be able to create records.',
    },
    [PermissionKey.TABLE_RECORD_DELETE]: {
        minimumRole: PermissionRole.EDITOR,
        label: 'Who can delete records',
        description: 'can delete records',
        userSelectorDescription: 'Only members selected here will be able to delete records.',
    },
    [PermissionKey.RECORD_FIELD_EDIT]: {
        minimumRole: PermissionRole.EDITOR,
        label: 'Who can edit data in this field',
        description: 'can edit records',
        userSelectorDescription: 'Only members selected here will be able to edit values in the {{field}} field.',
    },
    [PermissionKey.DOCUMENT_VISIBILITY]: {
        minimumRole: PermissionRole.VIEWER,
        label: 'Who can view this page',
        description: 'can view page',
        userSelectorDescription: 'Only members selected here will be able to view this page and its children.',
    },
    [PermissionKey.DOCUMENT_EDIT]: {
        minimumRole: PermissionRole.EDITOR,
        label: 'Who can edit this page',
        description: 'can edit page',
        userSelectorDescription: 'Only members selected here will be able to edit this page.',
    },
};
// Restrictiveness order for document permission inheritance (lower = more permissive).
// SPECIFIC_USERS is ranked high (5) by convention: it is treated as more restrictive
// than role-based options because access is explicitly gated to a named set of users.
// Note: the actual restrictiveness of SPECIFIC_USERS depends on which users are
// selected, but for inheritance validation we use this fixed ranking.
export const PermissionOptionRestrictiveness = {
    [PermissionOptionValue.EVERYONE]: 0,
    [PermissionOptionValue.VIEWERS_AND_UP]: 1,
    [PermissionOptionValue.COMMENTERS_AND_UP]: 2,
    [PermissionOptionValue.EDITORS_AND_UP]: 3,
    [PermissionOptionValue.CREATORS_AND_UP]: 4,
    [PermissionOptionValue.SPECIFIC_USERS]: 5,
    [PermissionOptionValue.NOBODY]: 6,
};
/**
 * Returns true if `child` is at least as restrictive as `parent`.
 * Uses >= because equal restrictiveness is valid (child may match parent).
 *
 * Unknown values are treated as invalid and return false (fail-closed)
 * to prevent accidentally allowing a more-permissive child.
 */
export const isMoreRestrictive = (child, parent) => {
    const childLevel = PermissionOptionRestrictiveness[child];
    const parentLevel = PermissionOptionRestrictiveness[parent];
    // Fail-closed: unknown values are never considered "more restrictive"
    if (childLevel === undefined || parentLevel === undefined) {
        return false;
    }
    return childLevel >= parentLevel;
};
export const DOCUMENT_PERMISSION_KEYS = [
    PermissionKey.DOCUMENT_VISIBILITY,
    PermissionKey.DOCUMENT_EDIT,
];
// Utility functions for permission management
export const getPermissionOption = (value) => {
    return PermissionOptions.find((option) => option.value === value);
};
export const getPermissionLabel = (value) => {
    var _a;
    return ((_a = getPermissionOption(value)) === null || _a === void 0 ? void 0 : _a.label) || 'Editors & up';
};
export const getPermissionIcon = (value) => {
    var _a;
    return ((_a = getPermissionOption(value)) === null || _a === void 0 ? void 0 : _a.icon) || 'role_editor';
};
export const getPermissionOptionValue = (grantedType, grantedRole) => {
    if (grantedType === PermissionGrantedType.ROLE) {
        if (grantedRole === PermissionRole.VIEWER) {
            return PermissionOptionValue.VIEWERS_AND_UP;
        }
        else if (grantedRole === PermissionRole.COMMENTER) {
            return PermissionOptionValue.COMMENTERS_AND_UP;
        }
        else if (grantedRole === PermissionRole.CREATOR) {
            return PermissionOptionValue.CREATORS_AND_UP;
        }
        else {
            return PermissionOptionValue.EDITORS_AND_UP;
        }
    }
    else if (grantedType === PermissionGrantedType.USER) {
        return PermissionOptionValue.SPECIFIC_USERS;
    }
    else if (grantedType === PermissionGrantedType.NOBODY) {
        return PermissionOptionValue.NOBODY;
    }
    // Default for table visibility is everyone, for others it's editors and up
    return PermissionOptionValue.EDITORS_AND_UP;
};
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiaW5kZXguanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi8uLi9zcmMvbGliL3Blcm1pc3Npb24vaW5kZXgudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUEsT0FBTyxFQUFFLFlBQVksRUFBRSxrQkFBa0IsRUFBRSxNQUFNLFVBQVUsQ0FBQztBQUk1RCxNQUFNLENBQU4sSUFBWSxhQU9YO0FBUEQsV0FBWSxhQUFhO0lBQ3ZCLHNEQUFxQyxDQUFBO0lBQ3JDLHNEQUFxQyxDQUFBO0lBQ3JDLDREQUEyQyxDQUFBO0lBQzNDLHdEQUF1QyxDQUFBO0lBQ3ZDLDREQUEyQyxDQUFBO0lBQzNDLGdEQUErQixDQUFBO0FBQ2pDLENBQUMsRUFQVyxhQUFhLEtBQWIsYUFBYSxRQU94QjtBQUVELE1BQU0sQ0FBTixJQUFZLHFCQUlYO0FBSkQsV0FBWSxxQkFBcUI7SUFDL0Isc0NBQWEsQ0FBQTtJQUNiLHNDQUFhLENBQUE7SUFDYiwwQ0FBaUIsQ0FBQTtBQUNuQixDQUFDLEVBSlcscUJBQXFCLEtBQXJCLHFCQUFxQixRQUloQztBQUVELE1BQU0sQ0FBTixJQUFZLGdCQUlYO0FBSkQsV0FBWSxnQkFBZ0I7SUFDMUIsbUNBQWUsQ0FBQTtJQUNmLG1DQUFlLENBQUE7SUFDZix5Q0FBcUIsQ0FBQTtBQUN2QixDQUFDLEVBSlcsZ0JBQWdCLEtBQWhCLGdCQUFnQixRQUkzQjtBQUVELE1BQU0sQ0FBTixJQUFZLGNBTVg7QUFORCxXQUFZLGNBQWM7SUFDeEIsaUNBQWUsQ0FBQTtJQUNmLHFDQUFtQixDQUFBO0lBQ25CLG1DQUFpQixDQUFBO0lBQ2pCLHlDQUF1QixDQUFBO0lBQ3ZCLG1DQUFpQixDQUFBO0FBQ25CLENBQUMsRUFOVyxjQUFjLEtBQWQsY0FBYyxRQU16QjtBQUVELHVEQUF1RDtBQUN2RCxNQUFNLENBQU4sSUFBWSxxQkFRWDtBQVJELFdBQVkscUJBQXFCO0lBQy9CLDBEQUFpQyxDQUFBO0lBQ2pDLGdFQUF1QyxDQUFBO0lBQ3ZDLDBEQUFpQyxDQUFBO0lBQ2pDLDREQUFtQyxDQUFBO0lBQ25DLDBEQUFpQyxDQUFBO0lBQ2pDLDBDQUFpQixDQUFBO0lBQ2pCLDhDQUFxQixDQUFBO0FBQ3ZCLENBQUMsRUFSVyxxQkFBcUIsS0FBckIscUJBQXFCLFFBUWhDO0FBVUQsTUFBTSxDQUFDLE1BQU0saUJBQWlCLEdBQXVCO0lBQ25EO1FBQ0UsS0FBSyxFQUFFLHFCQUFxQixDQUFDLGVBQWU7UUFDNUMsS0FBSyxFQUFFLGVBQWU7UUFDdEIsV0FBVyxFQUFFLG9DQUFvQztRQUNqRCxJQUFJLEVBQUUsY0FBYztLQUNyQjtJQUNEO1FBQ0UsS0FBSyxFQUFFLHFCQUFxQixDQUFDLGNBQWM7UUFDM0MsS0FBSyxFQUFFLGNBQWM7UUFDckIsV0FBVyxFQUFFLDRDQUE0QztRQUN6RCxJQUFJLEVBQUUsYUFBYTtRQUNuQixTQUFTLEVBQUUsSUFBSTtLQUNoQjtJQUNEO1FBQ0UsS0FBSyxFQUFFLHFCQUFxQixDQUFDLGNBQWM7UUFDM0MsS0FBSyxFQUFFLGdCQUFnQjtRQUN2QixXQUFXLEVBQ1QsK0RBQStEO1FBQ2pFLElBQUksRUFBRSxhQUFhO0tBQ3BCO0lBQ0Q7UUFDRSxLQUFLLEVBQUUscUJBQXFCLENBQUMsY0FBYztRQUMzQyxLQUFLLEVBQUUsZ0JBQWdCO1FBQ3ZCLFdBQVcsRUFBRSx5QkFBeUI7UUFDdEMsSUFBSSxFQUFFLFNBQVM7S0FDaEI7SUFDRDtRQUNFLEtBQUssRUFBRSxxQkFBcUIsQ0FBQyxRQUFRO1FBQ3JDLEtBQUssRUFBRSxVQUFVO1FBQ2pCLFdBQVcsRUFBRSx3QkFBd0I7UUFDckMsSUFBSSxFQUFFLFNBQVM7UUFDZixTQUFTLEVBQUUsSUFBSTtLQUNoQjtJQUNEO1FBQ0UsS0FBSyxFQUFFLHFCQUFxQixDQUFDLE1BQU07UUFDbkMsS0FBSyxFQUFFLFFBQVE7UUFDZixXQUFXLEVBQUUsc0JBQXNCO1FBQ25DLElBQUksRUFBRSxnQkFBZ0I7S0FDdkI7Q0FDRixDQUFDO0FBRUYsTUFBTSxDQUFDLE1BQU0sbUJBQW1CLEdBQUc7SUFDakMsQ0FBQyxjQUFjLENBQUMsS0FBSyxDQUFDLEVBQUUsQ0FBQztJQUN6QixDQUFDLGNBQWMsQ0FBQyxPQUFPLENBQUMsRUFBRSxDQUFDO0lBQzNCLENBQUMsY0FBYyxDQUFDLE1BQU0sQ0FBQyxFQUFFLENBQUM7SUFDMUIsQ0FBQyxjQUFjLENBQUMsU0FBUyxDQUFDLEVBQUUsQ0FBQztJQUM3QixDQUFDLGNBQWMsQ0FBQyxNQUFNLENBQUMsRUFBRSxDQUFDO0NBQzNCLENBQUM7QUFFRixNQUFNLENBQUMsTUFBTSxpQkFBaUIsR0FBRztJQUMvQixDQUFDLFlBQVksQ0FBQyxLQUFLLENBQUMsRUFBRSxjQUFjLENBQUMsS0FBSztJQUMxQyxDQUFDLFlBQVksQ0FBQyxPQUFPLENBQUMsRUFBRSxjQUFjLENBQUMsT0FBTztJQUM5QyxDQUFDLFlBQVksQ0FBQyxNQUFNLENBQUMsRUFBRSxjQUFjLENBQUMsTUFBTTtJQUM1QyxDQUFDLFlBQVksQ0FBQyxTQUFTLENBQUMsRUFBRSxjQUFjLENBQUMsU0FBUztJQUNsRCxDQUFDLFlBQVksQ0FBQyxNQUFNLENBQUMsRUFBRSxjQUFjLENBQUMsTUFBTTtJQUM1QyxDQUFDLGtCQUFrQixDQUFDLEtBQUssQ0FBQyxFQUFFLGNBQWMsQ0FBQyxLQUFLO0lBQ2hELENBQUMsa0JBQWtCLENBQUMsT0FBTyxDQUFDLEVBQUUsY0FBYyxDQUFDLE9BQU87SUFDcEQsQ0FBQyxrQkFBa0IsQ0FBQyxNQUFNLENBQUMsRUFBRSxjQUFjLENBQUMsTUFBTTtJQUNsRCxDQUFDLGtCQUFrQixDQUFDLFNBQVMsQ0FBQyxFQUFFLGNBQWMsQ0FBQyxTQUFTO0lBQ3hELENBQUMsa0JBQWtCLENBQUMsTUFBTSxDQUFDLEVBQUUsY0FBYyxDQUFDLE1BQU07Q0FDbkQsQ0FBQztBQUVGLE1BQU0sQ0FBQyxNQUFNLGNBQWMsR0FBRztJQUM1QixDQUFDLGFBQWEsQ0FBQyxnQkFBZ0IsQ0FBQyxFQUFFO1FBQ2hDLFdBQVcsRUFBRSxjQUFjLENBQUMsTUFBTTtRQUNsQyxLQUFLLEVBQUUsb0JBQW9CO1FBQzNCLFdBQVcsRUFBRSxnQkFBZ0I7UUFDN0IsdUJBQXVCLEVBQ3JCLHdFQUF3RTtLQUMzRTtJQUNELENBQUMsYUFBYSxDQUFDLGdCQUFnQixDQUFDLEVBQUU7UUFDaEMsV0FBVyxFQUFFLGNBQWMsQ0FBQyxNQUFNO1FBQ2xDLEtBQUssRUFBRSx3QkFBd0I7UUFDL0IsV0FBVyxFQUFFLG9CQUFvQjtRQUNqQyx1QkFBdUIsRUFDckIsNERBQTREO0tBQy9EO0lBQ0QsQ0FBQyxhQUFhLENBQUMsbUJBQW1CLENBQUMsRUFBRTtRQUNuQyxXQUFXLEVBQUUsY0FBYyxDQUFDLE1BQU07UUFDbEMsS0FBSyxFQUFFLHdCQUF3QjtRQUMvQixXQUFXLEVBQUUsb0JBQW9CO1FBQ2pDLHVCQUF1QixFQUNyQiw0REFBNEQ7S0FDL0Q7SUFDRCxDQUFDLGFBQWEsQ0FBQyxpQkFBaUIsQ0FBQyxFQUFFO1FBQ2pDLFdBQVcsRUFBRSxjQUFjLENBQUMsTUFBTTtRQUNsQyxLQUFLLEVBQUUsaUNBQWlDO1FBQ3hDLFdBQVcsRUFBRSxrQkFBa0I7UUFDL0IsdUJBQXVCLEVBQ3JCLGdGQUFnRjtLQUNuRjtJQUNELENBQUMsYUFBYSxDQUFDLG1CQUFtQixDQUFDLEVBQUU7UUFDbkMsV0FBVyxFQUFFLGNBQWMsQ0FBQyxNQUFNO1FBQ2xDLEtBQUssRUFBRSx3QkFBd0I7UUFDL0IsV0FBVyxFQUFFLGVBQWU7UUFDNUIsdUJBQXVCLEVBQ3JCLDZFQUE2RTtLQUNoRjtJQUNELENBQUMsYUFBYSxDQUFDLGFBQWEsQ0FBQyxFQUFFO1FBQzdCLFdBQVcsRUFBRSxjQUFjLENBQUMsTUFBTTtRQUNsQyxLQUFLLEVBQUUsd0JBQXdCO1FBQy9CLFdBQVcsRUFBRSxlQUFlO1FBQzVCLHVCQUF1QixFQUNyQiw0REFBNEQ7S0FDL0Q7Q0FDRixDQUFDO0FBRUYsdUZBQXVGO0FBQ3ZGLHFGQUFxRjtBQUNyRixzRkFBc0Y7QUFDdEYsZ0ZBQWdGO0FBQ2hGLHNFQUFzRTtBQUN0RSxNQUFNLENBQUMsTUFBTSwrQkFBK0IsR0FBMkI7SUFDckUsQ0FBQyxxQkFBcUIsQ0FBQyxRQUFRLENBQUMsRUFBRSxDQUFDO0lBQ25DLENBQUMscUJBQXFCLENBQUMsY0FBYyxDQUFDLEVBQUUsQ0FBQztJQUN6QyxDQUFDLHFCQUFxQixDQUFDLGlCQUFpQixDQUFDLEVBQUUsQ0FBQztJQUM1QyxDQUFDLHFCQUFxQixDQUFDLGNBQWMsQ0FBQyxFQUFFLENBQUM7SUFDekMsQ0FBQyxxQkFBcUIsQ0FBQyxlQUFlLENBQUMsRUFBRSxDQUFDO0lBQzFDLENBQUMscUJBQXFCLENBQUMsY0FBYyxDQUFDLEVBQUUsQ0FBQztJQUN6QyxDQUFDLHFCQUFxQixDQUFDLE1BQU0sQ0FBQyxFQUFFLENBQUM7Q0FDbEMsQ0FBQztBQUVGOzs7Ozs7R0FNRztBQUNILE1BQU0sQ0FBQyxNQUFNLGlCQUFpQixHQUFHLENBQy9CLEtBQTRCLEVBQzVCLE1BQTZCLEVBQ3BCLEVBQUU7SUFDWCxNQUFNLFVBQVUsR0FBRywrQkFBK0IsQ0FBQyxLQUFLLENBQUMsQ0FBQztJQUMxRCxNQUFNLFdBQVcsR0FBRywrQkFBK0IsQ0FBQyxNQUFNLENBQUMsQ0FBQztJQUU1RCxzRUFBc0U7SUFDdEUsSUFBSSxVQUFVLEtBQUssU0FBUyxJQUFJLFdBQVcsS0FBSyxTQUFTLEVBQUUsQ0FBQztRQUMxRCxPQUFPLEtBQUssQ0FBQztJQUNmLENBQUM7SUFFRCxPQUFPLFVBQVUsSUFBSSxXQUFXLENBQUM7QUFDbkMsQ0FBQyxDQUFDO0FBRUYsTUFBTSxDQUFDLE1BQU0sd0JBQXdCLEdBQUc7SUFDdEMsYUFBYSxDQUFDLG1CQUFtQjtJQUNqQyxhQUFhLENBQUMsYUFBYTtDQUM1QixDQUFDO0FBRUYsOENBQThDO0FBQzlDLE1BQU0sQ0FBQyxNQUFNLG1CQUFtQixHQUFHLENBQ2pDLEtBQWEsRUFDaUIsRUFBRTtJQUNoQyxPQUFPLGlCQUFpQixDQUFDLElBQUksQ0FBQyxDQUFDLE1BQU0sRUFBRSxFQUFFLENBQUMsTUFBTSxDQUFDLEtBQUssS0FBSyxLQUFLLENBQUMsQ0FBQztBQUNwRSxDQUFDLENBQUM7QUFFRixNQUFNLENBQUMsTUFBTSxrQkFBa0IsR0FBRyxDQUFDLEtBQWEsRUFBVSxFQUFFOztJQUMxRCxPQUFPLENBQUEsTUFBQSxtQkFBbUIsQ0FBQyxLQUFLLENBQUMsMENBQUUsS0FBSyxLQUFJLGNBQWMsQ0FBQztBQUM3RCxDQUFDLENBQUM7QUFFRixNQUFNLENBQUMsTUFBTSxpQkFBaUIsR0FBRyxDQUFDLEtBQWEsRUFBVSxFQUFFOztJQUN6RCxPQUFPLENBQUEsTUFBQSxtQkFBbUIsQ0FBQyxLQUFLLENBQUMsMENBQUUsSUFBSSxLQUFJLGFBQWEsQ0FBQztBQUMzRCxDQUFDLENBQUM7QUFFRixNQUFNLENBQUMsTUFBTSx3QkFBd0IsR0FBRyxDQUN0QyxXQUFrQyxFQUNsQyxXQUE0QixFQUNMLEVBQUU7SUFDekIsSUFBSSxXQUFXLEtBQUsscUJBQXFCLENBQUMsSUFBSSxFQUFFLENBQUM7UUFDL0MsSUFBSSxXQUFXLEtBQUssY0FBYyxDQUFDLE1BQU0sRUFBRSxDQUFDO1lBQzFDLE9BQU8scUJBQXFCLENBQUMsY0FBYyxDQUFDO1FBQzlDLENBQUM7YUFBTSxJQUFJLFdBQVcsS0FBSyxjQUFjLENBQUMsU0FBUyxFQUFFLENBQUM7WUFDcEQsT0FBTyxxQkFBcUIsQ0FBQyxpQkFBaUIsQ0FBQztRQUNqRCxDQUFDO2FBQU0sSUFBSSxXQUFXLEtBQUssY0FBYyxDQUFDLE9BQU8sRUFBRSxDQUFDO1lBQ2xELE9BQU8scUJBQXFCLENBQUMsZUFBZSxDQUFDO1FBQy9DLENBQUM7YUFBTSxDQUFDO1lBQ04sT0FBTyxxQkFBcUIsQ0FBQyxjQUFjLENBQUM7UUFDOUMsQ0FBQztJQUNILENBQUM7U0FBTSxJQUFJLFdBQVcsS0FBSyxxQkFBcUIsQ0FBQyxJQUFJLEVBQUUsQ0FBQztRQUN0RCxPQUFPLHFCQUFxQixDQUFDLGNBQWMsQ0FBQztJQUM5QyxDQUFDO1NBQU0sSUFBSSxXQUFXLEtBQUsscUJBQXFCLENBQUMsTUFBTSxFQUFFLENBQUM7UUFDeEQsT0FBTyxxQkFBcUIsQ0FBQyxNQUFNLENBQUM7SUFDdEMsQ0FBQztJQUNELDJFQUEyRTtJQUMzRSxPQUFPLHFCQUFxQixDQUFDLGNBQWMsQ0FBQztBQUM5QyxDQUFDLENBQUMifQ==